import { v4 as uuidv4 } from "uuid";
import { isDemoOrLocal } from "@/common/publicEnv";
import { HttpError } from "@/common/utils/errors/HttpError";
import { loginUser } from "@/common/utils/urlUtils";
import { normalizeTelemetryEndpoint } from "@/observability/routes";
import {
  FetchNetworkError,
  type FetchResponseFailureReason,
  FetchResponseParseError,
  isAbortError,
} from "./errors";

export interface FetchOptions {
  accessToken?: string;
  responseType?: "json" | "arraybuffer";
  personIdent?: string;
  orgnummer?: string;
}

export const AUTHORIZATION_HEADER = "Authorization";
export const NAV_PERSONIDENT_HEADER = "nav-personident";
export const ORGNUMMER_HEADER = "orgnummer";
export const TEST_SESSION_ID = "testscenario-session-id";

type ResponseType = NonNullable<FetchOptions["responseType"]>;

const defaultRequestHeaders = ({
  options,
  hasJsonBody,
}: {
  options?: FetchOptions;
  hasJsonBody: boolean;
}): Record<string, string> => {
  const headers: Record<string, string> = {};

  if (hasJsonBody) {
    headers["Content-Type"] = "application/json";
  }

  if (options?.accessToken) {
    headers[AUTHORIZATION_HEADER] = `Bearer ${options.accessToken}`;
  }

  if (options?.personIdent) {
    headers[NAV_PERSONIDENT_HEADER] = options.personIdent;
  }

  if (options?.orgnummer) {
    headers[ORGNUMMER_HEADER] = options.orgnummer;
  }

  if (isDemoOrLocal && typeof window !== "undefined") {
    let sessionId = localStorage.getItem(TEST_SESSION_ID);
    if (!sessionId) {
      sessionId = uuidv4();
      localStorage.setItem(TEST_SESSION_ID, sessionId);
    }
    headers[TEST_SESSION_ID] = sessionId;
  }

  return headers;
};

const parseJsonResponse = async <ResponseData>({
  response,
  requestUrl,
  method,
}: {
  response: Response;
  requestUrl: string;
  method: "GET" | "POST";
}): Promise<ResponseData> => {
  if (response.status === 204 || response.status === 205) {
    return undefined as ResponseData;
  }

  let text: string;
  try {
    text = await response.text();
  } catch (error) {
    if (isAbortError(error)) throw error;
    throwParseError(requestUrl, method, "body_read", error);
  }
  if (!text) {
    return undefined as ResponseData;
  }

  try {
    return JSON.parse(text) as ResponseData;
  } catch (error) {
    throwParseError(requestUrl, method, "invalid_json", error);
  }
};

const parseResponse = async <ResponseData>({
  response,
  responseType,
  requestUrl,
  method,
}: {
  response: Response;
  responseType?: FetchOptions["responseType"];
  requestUrl: string;
  method: "GET" | "POST";
}): Promise<ResponseData> => {
  if (responseType === "arraybuffer") {
    try {
      const buffer = await response.arrayBuffer();
      return new Uint8Array(buffer) as ResponseData;
    } catch (error) {
      if (isAbortError(error)) throw error;
      throwParseError(requestUrl, method, "body_read", error);
    }
  }

  return parseJsonResponse<ResponseData>({ response, requestUrl, method });
};

const throwHttpError = async (
  response: Response,
  requestUrl: string,
  method: "GET" | "POST",
): Promise<never> => {
  if (response.status === 401 && typeof window !== "undefined") {
    loginUser();
  }

  // Only this code is part of the reviewed API contract; never retain a body.
  let upstreamErrorCode: "SYKMELDT_NOT_FOUND" | undefined;
  if (response.status === 404) {
    const body: unknown = await response.json().catch((error: unknown) => {
      if (isAbortError(error)) throw error;
      return undefined;
    });
    if (
      typeof body === "object" &&
      body !== null &&
      "error_code" in body &&
      body.error_code === "SYKMELDT_NOT_FOUND"
    ) {
      upstreamErrorCode = "SYKMELDT_NOT_FOUND";
    }
  }
  throw new HttpError(
    response.status,
    `Request failed: method=${method} endpoint=${normalizeTelemetryEndpoint(requestUrl)} status=${response.status}`,
    { upstreamErrorCode },
  );
};

const throwNetworkError = (
  requestUrl: string,
  method: "GET" | "POST",
  cause: unknown,
): never => {
  throw new FetchNetworkError(
    `Network request failed: method=${method} endpoint=${normalizeTelemetryEndpoint(requestUrl)}`,
    // Browser telemetry serializes Error.cause; retain raw causes only on the server.
    typeof window === "undefined" ? { cause } : undefined,
  );
};

function throwParseError(
  requestUrl: string,
  method: "GET" | "POST",
  failureReason: FetchResponseFailureReason,
  cause: unknown,
): never {
  throw new FetchResponseParseError(
    `Response parsing failed: method=${method} endpoint=${normalizeTelemetryEndpoint(requestUrl)}`,
    failureReason,
    typeof window === "undefined" ? { cause } : undefined,
  );
}

const getServerAllowedOrigins = (): Set<string> => {
  // Server-side SSRF protection: restrict absolute URLs to known backends.
  // These env vars are not exposed to the browser bundle; we only use them on the server.
  const raw = [
    process.env.ISDIALOGMOTE_HOST,
    process.env.SYFOMOTEBEHOV_HOST,
    process.env.DINESYKMELDTE_BACKEND_HOST,
  ].filter(Boolean) as string[];

  const origins = new Set<string>();
  for (const value of raw) {
    try {
      origins.add(new URL(value).origin);
    } catch {
      // Ignore malformed env; better to not crash import.
    }
  }
  return origins;
};

const assertSafeServerRequestUrl = (url: string): void => {
  if (typeof window !== "undefined") {
    return;
  }

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    // Relative URL: not SSRF-able across origins.
    return;
  }

  if (parsed.username || parsed.password) {
    throw new Error("Blocked URL with credentials");
  }

  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    throw new Error(`Blocked non-http(s) URL: ${parsed.protocol}`);
  }

  const allowedOrigins = getServerAllowedOrigins();
  if (allowedOrigins.size > 0 && !allowedOrigins.has(parsed.origin)) {
    throw new Error(
      `Blocked server-side request to non-allowed origin: ${parsed.origin}`,
    );
  }
};

async function request<ResponseData>({
  url,
  method,
  data,
  options,
}: {
  url: string;
  method: "GET" | "POST";
  data?: unknown;
  options?: FetchOptions;
}): Promise<ResponseData> {
  const requestUrl = encodeURI(url);
  assertSafeServerRequestUrl(requestUrl);
  const hasJsonBody = method === "POST" && data !== undefined;

  let response: Response | undefined;
  try {
    response = await fetch(requestUrl, {
      method,
      body: hasJsonBody ? JSON.stringify(data) : undefined,
      headers: defaultRequestHeaders({ options, hasJsonBody }),
      credentials: "include",
    });
  } catch (error) {
    if (isAbortError(error)) throw error;
    throwNetworkError(requestUrl, method, error);
  }

  if (!response) {
    throw new Error("Fetch failed before receiving a response");
  }

  if (!response.ok) {
    await throwHttpError(response, requestUrl, method);
  }

  return parseResponse<ResponseData>({
    response,
    responseType: options?.responseType,
    requestUrl,
    method,
  });
}

type ResponseFor<T extends ResponseType, R> = T extends "arraybuffer"
  ? Uint8Array
  : R;

export const get = async <ResponseData, T extends ResponseType = "json">(
  url: string,
  options?: Omit<FetchOptions, "responseType"> & { responseType?: T },
): Promise<ResponseFor<T, ResponseData>> => {
  return request<ResponseFor<T, ResponseData>>({
    url,
    method: "GET",
    options,
  });
};

export const post = async <ResponseData, T extends ResponseType = "json">(
  url: string,
  data?: unknown,
  options?: Omit<FetchOptions, "responseType"> & { responseType?: T },
): Promise<ResponseFor<T, ResponseData>> => {
  return request<ResponseFor<T, ResponseData>>({
    url,
    method: "POST",
    data,
    options,
  });
};
